import { useEffect, useId, useRef } from "react";
import { appConfig } from "../lib/config";

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
    };
  }
}

interface TurnstileProps {
  onToken: (token: string) => void;
}

export function Turnstile({ onToken }: TurnstileProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | undefined>(undefined);
  const reactId = useId();

  useEffect(() => {
    if (!appConfig.turnstileSiteKey || !elementRef.current) return;

    let cancelled = false;
    const renderWidget = () => {
      if (cancelled || !elementRef.current || !window.turnstile || widgetId.current) return;
      widgetId.current = window.turnstile.render(elementRef.current, {
        sitekey: appConfig.turnstileSiteKey,
        callback: onToken,
        "expired-callback": () => onToken(""),
        "error-callback": () => onToken(""),
        theme: "light",
      });
    };

    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile="true"]');
    if (existing) {
      existing.addEventListener("load", renderWidget);
      renderWidget();
    } else {
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.turnstile = "true";
      script.addEventListener("load", renderWidget);
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = undefined;
    };
  }, [onToken]);

  if (!appConfig.turnstileSiteKey) return null;
  return <div id={`turnstile-${reactId}`} ref={elementRef} className="turnstile" />;
}
